import { useGLTF, MeshRefractionMaterial } from '@react-three/drei';
import React, { useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';

export default function Ring({ envRotationY, scale, environmentMap, ...props }) {
  const { nodes } = useGLTF('/Ring_2-transformed.glb');
  const diamondRef = useRef();
  const ringBodyRef = useRef();
  const { gl, scene } = useThree();

  // Initialize the cube render target and cube camera
  const cubeRenderTarget = useMemo(
    () =>
      new THREE.WebGLCubeRenderTarget(256, {
        format: THREE.RGBAFormat,
        generateMipmaps: true,
        minFilter: THREE.LinearMipmapLinearFilter,
        type: THREE.HalfFloatType, // Use HalfFloatType for HDR
      }),
    []
  );

  const cubeCamera = useMemo(
    () => new THREE.CubeCamera(0.1, 1000, cubeRenderTarget),
    [cubeRenderTarget]
  );

  // Create a new scene for the cube camera
  const cubeCameraScene = useMemo(() => new THREE.Scene(), []);

  // Create the environment sphere
  const envSphere = useMemo(() => {
    const geometry = new THREE.SphereGeometry(100, 32, 32);
    const material = new THREE.MeshBasicMaterial({
      map: environmentMap,
      side: THREE.BackSide,
      toneMapped: false,
    });
    const mesh = new THREE.Mesh(geometry, material);
    cubeCameraScene.add(mesh);
    return mesh;
  }, [environmentMap, cubeCameraScene]);

  // Initialize PMREMGenerator
  const pmremGenerator = useMemo(() => new THREE.PMREMGenerator(gl), [gl]);
  pmremGenerator.compileCubemapShader();

  const pmremRenderTarget = useRef();

  const updateEnvMap = useRef(true);

  useEffect(() => {
    updateEnvMap.current = true;
  }, [envRotationY]);

  useFrame(() => {
    if (updateEnvMap.current && cubeCamera) {
      // Rotate the environment sphere
      envSphere.rotation.y = envRotationY;

      // Update the cube camera to capture the environment
      cubeCamera.update(gl, cubeCameraScene);

      // Dispose of the previous PMREM render target if it exists
      if (pmremRenderTarget.current) {
        pmremRenderTarget.current.dispose();
      }

      // Process the cube render target with PMREM
      pmremRenderTarget.current = pmremGenerator.fromCubemap(cubeRenderTarget.texture);

      // Update the ring body's material envMap with the PMREM-processed texture
      if (ringBodyRef.current) {
        ringBodyRef.current.material.envMap = pmremRenderTarget.current.texture;
        ringBodyRef.current.material.needsUpdate = true;
      }

      // Update the scene's environment
      scene.environment = pmremRenderTarget.current.texture;

      updateEnvMap.current = false;
    }
  });

  useEffect(() => {
    return () => {
      // Clean up PMREMGenerator and render targets
      pmremGenerator.dispose();
      if (pmremRenderTarget.current) {
        pmremRenderTarget.current.dispose();
      }
    };
  }, [pmremGenerator]);

  if (!environmentMap) {
    return null;
  }

  return (
    <group {...props} dispose={null}>
      {/* Use the cube camera to generate the envMap for the diamond */}
      <primitive object={cubeCamera} />

      {/* Diamond Mesh */}
      <mesh
        ref={diamondRef}
        scale={[scale, scale, scale]}
        geometry={nodes.Diamond.geometry}
      >
        <MeshRefractionMaterial
          envMap={cubeRenderTarget.texture}
          bounces={3}
          ior={2.4}
          fresnel={1}
          aberrationStrength={0.01}
          color="#ffffff"
          toneMapped={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Ring Body */}
      <mesh
        ref={ringBodyRef}
        scale={[scale, scale, scale]}
        castShadow
        receiveShadow
        geometry={nodes['RingBody'].geometry}
      >
        <meshPhysicalMaterial
          metalness={1.0}
          roughness={0.15}
          envMapIntensity={1.0}
        />
      </mesh>
    </group>
  );
}
